const AUTH_BASE_URL =
  import.meta.env.VITE_AUTH_API_URL || 'http://localhost:3005/api/v1'

export interface LoginRequest {
  email: string
  password: string
  rememberMe?: boolean
}

export interface PasswordResetRequest {
  token: string
  password: string
}

export interface ForgotPasswordRequest {
  email: string
}

export interface EmailVerificationRequest {
  token: string
}

export interface RegisterRequest {
  email: string
  password: string
  firstName: string
  lastName: string
  organizationName: string
  subscriptionTierId?: number
}

export interface AcceptInvitationRequest {
  email: string
  password: string
  firstName: string
  lastName: string
  invitationToken: string
}

export interface User {
  id: string
  email: string
  firstName: string
  lastName: string
  roleId: number
  roleName: string
  organizationId: string
  organizationName: string
  customerId?: string
  subscriptionTier: string
  subscriptionStatus?: 'active' | 'trialing' | 'past_due' | 'canceled' | 'suspended' | null
  permissions?: string[]
  lastLoginAt?: string
  onboardingStatus?: 'pending_verification' | 'verified_unpaid' | 'payment_pending' | 'completed' | 'admin_created'
  hasCompletedFirstTimeSetup?: boolean
}

export interface Role {
  id: number
  roleName: string
  roleDescription: string
  permissions: string[]
}

export interface RolesResponse {
  success: boolean
  data: Role[]
}

// Legacy type for backward compatibility
export interface LoginCredentials {
  email: string
  password: string
  rememberMe?: boolean
}

export interface AuthResponse {
  success: boolean
  message?: string
  user?: User
  session?: {
    expiresAt: string
  }
  error?: {
    code: string
    message: string
    field?: string
  }
  redirectUrl?: string
}

export interface StatusResponse {
  success: boolean
  authenticated: boolean
  user: User | null
  system: {
    environment: string
    authBypassEnabled: boolean
  }
}

class AuthService {
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const response = await fetch(`${AUTH_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include', // Important for session cookies
      body: JSON.stringify(credentials)
    })

    return await response.json()
  }

  async register(userData: RegisterRequest): Promise<AuthResponse> {
    const response = await fetch(`${AUTH_BASE_URL}/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify(userData)
    })

    return await response.json()
  }

  async acceptInvitation(
    invitationData: AcceptInvitationRequest
  ): Promise<AuthResponse> {
    const response = await fetch(`${AUTH_BASE_URL}/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      // Don't include credentials to prevent auto-login
      body: JSON.stringify(invitationData)
    })

    return await response.json()
  }

  async logout(): Promise<AuthResponse> {
    const response = await fetch(`${AUTH_BASE_URL}/auth/logout`, {
      method: 'POST',
      credentials: 'include'
    })

    return await response.json()
  }

  async getCurrentUser(): Promise<AuthResponse> {
    const response = await fetch(`${AUTH_BASE_URL}/auth/me`, {
      credentials: 'include'
    })

    return await response.json()
  }

  async getStatus(): Promise<StatusResponse> {
    const response = await fetch(`${AUTH_BASE_URL}/auth/status`, {
      credentials: 'include'
    })

    return await response.json()
  }

  async refreshSession(): Promise<AuthResponse> {
    const response = await fetch(`${AUTH_BASE_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include'
    })

    return await response.json()
  }

  async getRoles(): Promise<RolesResponse> {
    const response = await fetch(
      `${AUTH_BASE_URL}/auth/roles?scope=organization`,
      {
        credentials: 'include'
      }
    )

    return await response.json()
  }

  async resetPassword(resetData: PasswordResetRequest): Promise<AuthResponse> {
    const response = await fetch(`${AUTH_BASE_URL}/auth/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify(resetData)
    })

    return await response.json()
  }

  async requestPasswordReset(
    requestData: ForgotPasswordRequest
  ): Promise<AuthResponse> {
    const response = await fetch(`${AUTH_BASE_URL}/auth/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify(requestData)
    })

    return await response.json()
  }

  async verifyEmail(
    verificationData: EmailVerificationRequest
  ): Promise<AuthResponse> {
    const response = await fetch(`${AUTH_BASE_URL}/auth/verify-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      // SECURITY: Don't include credentials - email verification should NOT auto-login users
      // credentials: 'include',
      body: JSON.stringify(verificationData)
    })

    return await response.json()
  }

  /**
   * TODO: PRODUCTION - Implement actual email verification status endpoint
   * GET /api/v1/auth/verification-status
   *
   * This is a MOCK implementation for development
   * Polls the verification status during onboarding flow
   */
  async getVerificationStatus(email: string): Promise<{ verified: boolean }> {
    // MOCK: Check localStorage flag set by email verification
    const verificationFlag = localStorage.getItem('email_verification_status')

    if (verificationFlag) {
      const data = JSON.parse(verificationFlag)
      // Check if it's for this email and within valid time (10 minutes)
      if (data.email === email) {
        const verifiedAt = new Date(data.verifiedAt).getTime()
        const now = Date.now()
        const tenMinutes = 10 * 60 * 1000

        if (now - verifiedAt < tenMinutes) {
          return { verified: true }
        }
      }
    }

    return { verified: false }

    /* PRODUCTION CODE (commented out for now):
    const response = await fetch(
      `${AUTH_BASE_URL}/auth/verification-status?email=${encodeURIComponent(email)}`,
      {
        credentials: 'include',
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to check verification status');
    }

    return await response.json();
    */
  }

  /**
   * Resend email verification
   * This endpoint might already exist in your backend
   */
  async resendVerificationEmail(email: string): Promise<AuthResponse> {
    const response = await fetch(`${AUTH_BASE_URL}/auth/resend-verification`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({ email })
    })

    return await response.json()
  }
}

export const authService = new AuthService()
